import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, FileText } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import PublicTrustFooter from "@/components/PublicTrustFooter";

/**
 * Terms — public Terms of Use page mounted at `/terms`. Deliberately short and
 * plain-English so tradies actually read it before signing up. Matches the
 * Privacy page structure so both look like they belong to the same product.
 */
export default function Terms() {
  const nav = useNavigate();
  const { user } = useAuth();

  return (
    <div className="min-h-screen bi-grain bi-ambient text-white">
      <div className="max-w-[720px] mx-auto px-5 py-10 space-y-6" data-testid="terms-page">
        <button
          type="button"
          onClick={() => nav(-1)}
          data-testid="terms-back"
          className="inline-flex items-center gap-1 text-white/60 hover:text-white text-sm"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <header className="space-y-2">
          <div className="inline-flex items-center gap-2 h-8 px-3 rounded-full bg-[#FF9500]/15 text-[#FF9500] text-[10px] uppercase tracking-[0.2em] font-heading font-bold">
            <FileText className="w-3.5 h-3.5" />
            Terms of Use
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight leading-tight">
            BuildIntel AI &mdash; Terms of Use
          </h1>
          <p className="text-sm text-white/60">Last updated: July 2026</p>
        </header>

        <section
          className="bi-surface p-5 sm:p-6 space-y-5 text-[15px] leading-relaxed text-white/85"
          data-testid="terms-body"
        >
          <Clause n="1" title="What BuildIntel is">
            BuildIntel AI is a helper tool for Australian tradies to capture
            costs, track jobs, and prepare summaries for tax and accounting.
            It is not a registered tax agent, accountant, or bookkeeper.
          </Clause>

          <Clause n="2" title="Your responsibility">
            You are responsible for checking all figures before lodging
            anything with the ATO or sending them to your accountant. Always
            confirm important decisions with a registered tax agent or
            accountant.
          </Clause>

          <Clause n="3" title="No formal advice">
            Information in the app is general in nature. It is not personal
            tax, legal, or financial advice.
          </Clause>

          <Clause n="4" title="Your data">
            You own your data. We store it to provide the service. See our{" "}
            <Link
              to="/privacy"
              data-testid="terms-privacy-link"
              className="text-[#FF9500] hover:underline"
            >
              Privacy Policy
            </Link>{" "}
            for details. You can delete your account and data at any time.
          </Clause>

          <Clause n="5" title="Acceptable use">
            Don&apos;t misuse the app, attempt to break it, or use it for
            unlawful purposes.
          </Clause>

          <Clause n="6" title="Service availability">
            We aim to keep the app running, but we don&apos;t guarantee
            uninterrupted access.
          </Clause>

          <Clause n="7" title="Changes">
            We may update these Terms. Continued use means you accept the
            updated Terms.
          </Clause>

          <Clause n="8" title="Contact">
            Questions: use in-app feedback or email the address shown in the app.
          </Clause>
        </section>

        <div className="flex flex-wrap gap-2 pt-2">
          {user ? (
            <Link
              to="/settings"
              data-testid="terms-goto-settings"
              className="h-11 px-4 rounded-xl bi-ghost-btn inline-flex items-center gap-2 text-sm"
            >
              Back to Settings
            </Link>
          ) : (
            <Link
              to="/register"
              data-testid="terms-goto-register"
              className="h-11 px-4 rounded-xl bi-accent-btn inline-flex items-center gap-2 text-sm font-heading font-bold uppercase tracking-wider"
            >
              Continue to sign up
            </Link>
          )}
        </div>

        <PublicTrustFooter testId="terms-trust-footer" />
      </div>
    </div>
  );
}

// Tiny inline sub-component. Kept here (rather than in /components) because
// it's used only in this file and doing so avoids indirection for something
// that's essentially a small stylised heading + paragraph.
function Clause({ n, title, children }) {
  return (
    <div className="space-y-1.5">
      <h2 className="font-heading text-lg font-extrabold text-white">
        <span className="text-[#FF9500] mr-1.5">{n}.</span>
        {title}
      </h2>
      <p className="text-white/80">{children}</p>
    </div>
  );
}
